import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Spinner } from "react-bootstrap";
import * as actions from "../../../Redux/Actions/index";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../component/Toasts/Toasts";

const LOGIN_PORTAL_OPTIONS = [
  { label: "Icewarp (skill-mine.icewarpcloud.in)", value: "https://skill-mine.icewarpcloud.in/webmail/" },
  { label: "Microsoft 365 (m365.cloud.microsoft)", value: "https://m365.cloud.microsoft/" },
];

interface WelcomeEmailModalProps {
  employeeId: string;
  personalMail: string;
  hrbpRef?: string;
  managerRef?: string;
  onClose: () => void;
  onSendSuccess?: () => void;
}

interface EmployeeOption {
  _id: string;
  label: string;
  email: string;
}

const EmailChipInput = ({
  label,
  chips,
  onAdd,
  onRemove,
  options,
  loadingOptions,
}: {
  label: string;
  chips: string[];
  onAdd: (email: string) => void;
  onRemove: (email: string) => void;
  options: EmployeeOption[];
  loadingOptions: boolean;
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const filtered = options.filter(
    (o) =>
      !chips.includes(o.email) &&
      (o.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        o.email.toLowerCase().includes(inputValue.toLowerCase()))
  );

  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
      e.preventDefault();
      const email = inputValue.trim().replace(/,$/, "");
      if (email && isValidEmail(email) && !chips.includes(email)) {
        onAdd(email);
        setInputValue("");
        setShowDropdown(false);
      }
    }
    if (e.key === "Backspace" && !inputValue && chips.length > 0) {
      onRemove(chips[chips.length - 1]);
    }
  };

  const handleSelect = (opt: EmployeeOption) => {
    onAdd(opt.email);
    setInputValue("");
    setShowDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 12, fontWeight: 700, color: "#8a92a6", marginBottom: 7, display: "block", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </label>
      <div
        ref={wrapperRef}
        style={{
          position: "relative",
          border: "1.5px solid #d0d7e3",
          borderRadius: 7,
          background: "#fff",
          padding: "6px 8px",
          display: "flex",
          flexWrap: "wrap",
          gap: 5,
          cursor: "text",
          minHeight: 42,
        }}
        onClick={() => { setShowDropdown(true); }}
      >
        {chips.map((email) => (
          <span
            key={email}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              background: "#e8eef8", border: "1px solid #c5d0e8",
              borderRadius: 4, padding: "2px 8px", fontSize: 13, color: "#1e2a3b",
            }}
          >
            {email}
            <span
              onClick={(e) => { e.stopPropagation(); onRemove(email); }}
              style={{ cursor: "pointer", color: "#c0392b", fontWeight: 700, fontSize: 14, lineHeight: 1 }}
            >×</span>
          </span>
        ))}
        <input
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          placeholder={chips.length === 0 ? "Type name or email…" : ""}
          style={{
            border: "none", outline: "none", flex: 1, minWidth: 140,
            fontSize: 13, color: "#1e2a3b", background: "transparent", padding: "2px 4px",
          }}
        />
        {showDropdown && (
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
            background: "#fff", border: "1.5px solid #d0d7e3", borderRadius: 7,
            boxShadow: "0 4px 16px rgba(0,0,0,0.10)", maxHeight: 200, overflowY: "auto", marginTop: 2,
          }}>
            {loadingOptions ? (
              <div style={{ padding: "10px 14px", fontSize: 13, color: "#8a92a6" }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: "10px 14px", fontSize: 13, color: "#8a92a6" }}>
                {inputValue.trim() ? `Press Enter to add "${inputValue.trim()}"` : "No results"}
              </div>
            ) : (
              filtered.slice(0, 50).map((opt) => (
                <div
                  key={opt._id}
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
                  style={{
                    padding: "8px 14px", cursor: "pointer", fontSize: 13,
                    borderBottom: "1px solid #f0f3f8",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f4f7fd")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                >
                  <span style={{ fontWeight: 600, color: "#1e2a3b" }}>{opt.label}</span>
                  <span style={{ color: "#8a92a6", marginLeft: 6 }}>{opt.email}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      <p style={{ fontSize: 11, color: "#b0b8c8", margin: "4px 0 0 2px" }}>
        Pick from the list or type an email and press Enter
      </p>
    </div>
  );
};

const WelcomeEmailModal = ({ employeeId, personalMail, hrbpRef, managerRef, onClose, onSendSuccess }: WelcomeEmailModalProps) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { getEmployeeData } = useSelector((state: any) => state.employeeReducer);

  const [attachments, setAttachments] = useState<{ name: string; content: string }[]>([]);
  const [loginUrl, setLoginUrl] = useState(LOGIN_PORTAL_OPTIONS[0].value);
  const [loading, setLoading] = useState(false);

  const [toEmails, setToEmails] = useState<string[]>(personalMail ? [personalMail] : []);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [ccPrefilled, setCcPrefilled] = useState(false);

  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);

  const loadingOptions = !getEmployeeData;

  useEffect(() => {
    if (!getEmployeeData) {
      dispatch(actions.getEmployeeDataAction());
    }
  }, []);

  useEffect(() => {
    if (!getEmployeeData?.data) return;

    const list: EmployeeOption[] = getEmployeeData.data
      .filter((e: any) => e.emailId)
      .map((e: any) => ({
        _id: String(e._id),
        label: [e.candidateName, e.middleName, e.lastname].filter(Boolean).join(" ").trim() || e.emailId,
        email: e.emailId,
      }));
    setEmployeeOptions(list);

    if (!ccPrefilled) {
      const initialCc: string[] = [];
      const addCc = (email: string) => { if (email && !initialCc.includes(email)) initialCc.push(email); };
      if (hrbpRef) {
        const hrbp = getEmployeeData.data.find((e: any) => String(e._id) === String(hrbpRef));
        if (hrbp?.emailId) addCc(hrbp.emailId);
      }
      if (managerRef) {
        const manager = getEmployeeData.data.find((e: any) => String(e._id) === String(managerRef));
        if (manager?.emailId) addCc(manager.emailId);
      }
      setCcEmails(initialCc);
      setCcPrefilled(true);
    }
  }, [getEmployeeData]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    try {
      const converted = await Promise.all(
        files.map(
          (file) =>
            new Promise<{ name: string; content: string }>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const base64 = (reader.result as string).split(",")[1];
                resolve({ name: file.name, content: base64 });
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            })
        )
      );
      setAttachments((prev) => [...prev, ...converted]);
    } catch {
      showErrorToastMessage("Failed to read one or more files. Please try again.");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = () => {
    if (toEmails.length === 0) {
      showErrorToastMessage("Please add at least one recipient in the To field.");
      return;
    }
    setLoading(true);
    dispatch(
      actions.sendWelcomeEmailAction(
        employeeId,
        attachments,
        (res: any) => {
          setLoading(false);
          if (res?.error?.error_description) {
            showErrorToastMessage(res.error.error_description);
          } else if (res?.code === 200) {
            showSuccessToastMessage("Welcome email sent successfully");
            onSendSuccess ? onSendSuccess() : onClose();
          } else {
            showErrorToastMessage("Something went wrong. Please try again.");
          }
        },
        loginUrl,
        toEmails,
        ccEmails,
      )
    );
  };

  return (
    <Modal show onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton style={{ borderBottom: "2px solid #f0f3f8", padding: "18px 24px" }}>
        <Modal.Title style={{ fontSize: 17, fontWeight: 700, color: "#1e2a3b" }}>
          Send Welcome Email
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "20px 24px" }}>

        {/* From — read only */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#8a92a6", marginBottom: 7, display: "block", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            From
          </label>
          <div style={{
            border: "1.5px solid #d0d7e3", borderRadius: 7, background: "#f7f9fc",
            padding: "9px 12px", fontSize: 13, color: "#5a6785",
          }}>
            smtc-hr@skill-mine.com
          </div>
        </div>

        {/* Subject — read only */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#8a92a6", marginBottom: 7, display: "block", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Subject
          </label>
          <div style={{
            border: "1.5px solid #d0d7e3", borderRadius: 7, background: "#f7f9fc",
            padding: "9px 12px", fontSize: 13, color: "#5a6785",
          }}>
            Employee Code &amp; Official E-mail ID details
          </div>
        </div>

        {/* To */}
        <EmailChipInput
          label="To"
          chips={toEmails}
          onAdd={(e) => setToEmails((prev) => [...prev, e])}
          onRemove={(e) => setToEmails((prev) => prev.filter((x) => x !== e))}
          options={employeeOptions}
          loadingOptions={loadingOptions}
        />

        {/* CC */}
        <EmailChipInput
          label="CC"
          chips={ccEmails}
          onAdd={(e) => setCcEmails((prev) => [...prev, e])}
          onRemove={(e) => setCcEmails((prev) => prev.filter((x) => x !== e))}
          options={employeeOptions}
          loadingOptions={loadingOptions}
        />

        {/* Login Portal */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#8a92a6", marginBottom: 7, display: "block", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Login Portal
          </label>
          <select
            value={loginUrl}
            onChange={(e) => setLoginUrl(e.target.value)}
            style={{
              width: "100%", padding: "9px 12px", borderRadius: 7,
              border: "1.5px solid #d0d7e3", fontSize: 14, color: "#1e2a3b",
              background: "#fff", outline: "none", cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            {LOGIN_PORTAL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Attachments */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#8a92a6", marginBottom: 8, display: "block", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Attachments <span style={{ fontWeight: 400, color: "#b0b8c8", textTransform: "none", letterSpacing: 0 }}>(optional)</span>
          </label>
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              width: "100%", padding: "10px", borderRadius: 7,
              border: "1.5px dashed #b0bcd0", background: "#f7f9fc",
              color: "#5a6785", fontSize: 13, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Add Files
          </button>
          <input type="file" multiple ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />
          {attachments.length > 0 && (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
              {attachments.map((a, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 12px", borderRadius: 7,
                  background: "#eef2fb", border: "1px solid #d8e0f0", fontSize: 13,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
                    <span style={{ fontSize: 16 }}>📎</span>
                    <span style={{ color: "#1e2a3b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                  </div>
                  <span
                    onClick={() => removeAttachment(i)}
                    style={{ cursor: "pointer", color: "#c0392b", fontWeight: 700, fontSize: 16, lineHeight: 1, paddingLeft: 8, flexShrink: 0 }}
                  >×</span>
                </div>
              ))}
            </div>
          )}
        </div>

      </Modal.Body>

      <Modal.Footer style={{ borderTop: "2px solid #f0f3f8", padding: "14px 24px", gap: 10, justifyContent: "flex-end" }}>
        <Button
          variant="light"
          onClick={onClose}
          disabled={loading}
          style={{ fontSize: 13, fontWeight: 500, borderRadius: 7, padding: "8px 20px", color: "#5a6785", border: "1.5px solid #d0d7e3", background: "#fff" }}
        >
          Skip for now
        </Button>
        <Button
          variant="primary"
          onClick={handleSend}
          disabled={loading || toEmails.length === 0}
          style={{ fontSize: 13, fontWeight: 600, borderRadius: 7, padding: "8px 24px", minWidth: 110 }}
        >
          {loading ? <Spinner size="sm" animation="border" /> : "Send Email"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WelcomeEmailModal;
